(() => {
  const selector = '[data-estimated-delivery]';
  const timeZone = 'Europe/Bratislava';

  const getBratislavaNow = () => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());

    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));

    return {
      date: new Date(Date.UTC(Number(values.year), Number(values.month) - 1, Number(values.day))),
      hour: Number(values.hour),
      minute: Number(values.minute),
    };
  };

  const toIsoDate = (date) => date.toISOString().slice(0, 10);

  const addCalendarDays = (date, days) => {
    const next = new Date(date.getTime());
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  };

  const isWeekend = (date) => {
    const day = date.getUTCDay();
    return day === 0 || day === 6;
  };

  const isBusinessDay = (date, excludedDates) => !isWeekend(date) && !excludedDates.has(toIsoDate(date));

  const nextBusinessDay = (date, excludedDates) => {
    let next = new Date(date.getTime());

    while (!isBusinessDay(next, excludedDates)) {
      next = addCalendarDays(next, 1);
    }

    return next;
  };

  const addBusinessDays = (date, days, excludedDates) => {
    let result = new Date(date.getTime());
    let remaining = Math.max(days, 0);

    while (remaining > 0) {
      result = addCalendarDays(result, 1);

      if (isBusinessDay(result, excludedDates)) {
        remaining -= 1;
      }
    }

    return result;
  };

  const parseInteger = (value, fallback) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const parseExcludedDates = (value) =>
    new Set(
      String(value || '')
        .split(',')
        .map((date) => date.trim())
        .filter((date) => /^\d{4}-\d{2}-\d{2}$/.test(date))
    );

  const getLanguage = (element) => {
    const locale = (element.dataset.locale || document.documentElement.lang || 'sk').toLowerCase();
    return locale.startsWith('cs') ? 'cs' : 'sk';
  };

  const formatFullDate = (date, language) =>
    new Intl.DateTimeFormat(language === 'cs' ? 'cs-CZ' : 'sk-SK', {
      timeZone: 'UTC',
      day: 'numeric',
      month: 'long',
    }).format(date);

  const formatNumericDate = (date) => `${date.getUTCDate()}. ${date.getUTCMonth() + 1}.`;

  const isSameMonthAndYear = (start, end) =>
    start.getUTCFullYear() === end.getUTCFullYear() && start.getUTCMonth() === end.getUTCMonth();

  const isSameDate = (start, end) => toIsoDate(start) === toIsoDate(end);

  const formatProductRange = (start, end, language) => {
    if (isSameDate(start, end)) return formatFullDate(start, language);

    if (isSameMonthAndYear(start, end)) {
      return language === 'cs'
        ? `${start.getUTCDate()}.–${formatFullDate(end, language)}`
        : `${start.getUTCDate()}. – ${formatFullDate(end, language)}`;
    }

    return language === 'cs'
      ? `${formatFullDate(start, language)}–${formatFullDate(end, language)}`
      : `${formatFullDate(start, language)} – ${formatFullDate(end, language)}`;
  };

  const formatCardRange = (start, end, language) => {
    if (isSameDate(start, end)) return formatNumericDate(start);

    if (isSameMonthAndYear(start, end)) {
      return language === 'cs'
        ? `${start.getUTCDate()}.–${end.getUTCDate()}. ${end.getUTCMonth() + 1}.`
        : `${start.getUTCDate()}. – ${end.getUTCDate()}. ${end.getUTCMonth() + 1}.`;
    }

    return language === 'cs'
      ? `${formatNumericDate(start)}–${formatNumericDate(end)}`
      : `${formatNumericDate(start)} – ${formatNumericDate(end)}`;
  };

  const calculateDateRange = (element) => {
    let minDays = parseInteger(element.dataset.minBusinessDays, 2);
    let maxDays = parseInteger(element.dataset.maxBusinessDays, 4);
    const cutoffHour = Math.min(Math.max(parseInteger(element.dataset.cutoffHour, 12), 0), 23);
    const excludedDates = parseExcludedDates(element.dataset.excludedDates);
    const now = getBratislavaNow();
    let startDate = now.date;

    if (now.hour >= cutoffHour) {
      startDate = addCalendarDays(startDate, 1);
    }

    startDate = nextBusinessDay(startDate, excludedDates);
    minDays = Math.max(minDays, 0);
    maxDays = Math.max(maxDays, minDays);

    return {
      minDate: addBusinessDays(startDate, minDays, excludedDates),
      maxDate: addBusinessDays(startDate, maxDays, excludedDates),
    };
  };

  const renderEstimatedDelivery = (element) => {
    const dateElement = element.querySelector('[data-estimated-delivery-date]');
    if (!dateElement) return;

    const { minDate, maxDate } = calculateDateRange(element);
    const language = getLanguage(element);
    const context = element.dataset.estimatedDeliveryContext;
    const dateText =
      context === 'product'
        ? formatProductRange(minDate, maxDate, language)
        : formatCardRange(minDate, maxDate, language);

    dateElement.textContent = dateText;
    element.hidden = false;
    element.dataset.estimatedDeliveryReady = 'true';
  };

  const getEstimatedDeliveryElements = (root = document) => {
    const elements = [];

    if (root.matches?.(selector)) {
      elements.push(root);
    }

    root.querySelectorAll?.(selector).forEach((element) => elements.push(element));

    return elements.filter((element) => !element.dataset.estimatedDeliveryReady);
  };

  const initEstimatedDeliveries = (root = document) => {
    getEstimatedDeliveryElements(root).forEach(renderEstimatedDelivery);
  };

  const observeEstimatedDeliveries = () => {
    if (!window.MutationObserver) return;

    const observer = new window.MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            initEstimatedDeliveries(node);
          }
        });
      });
    });

    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initEstimatedDeliveries();
        observeEstimatedDeliveries();
      },
      { once: true }
    );
  } else {
    initEstimatedDeliveries();
    observeEstimatedDeliveries();
  }

  document.addEventListener('shopify:section:load', (event) => initEstimatedDeliveries(event.target));
})();
